// src/services/jobListingsService.js - ACTUALIZADO
import supabase from '../supabase';
import NotificationManager from '../utils/notificationManager';

const logger = {
  info: (message, data = null) => console.log(`ℹ️ [JobListingsService] ${message}`, data || ''),
  success: (message, data = null) => console.log(`✅ [JobListingsService] ${message}`, data || ''),
  error: (message, error = null) => console.error(`❌ [JobListingsService] ${message}`, error || '')
};

const getJobListings = async (filters = {}) => {
  try {
    logger.info('Obteniendo ofertas de trabajo', filters);
    
    let query = supabase
      .from('job_offers')
      .select(`
        *,
        creator:company_profiles(company_name, logo)
      `);
    
    if (filters.id) {
      query = query.eq('id', filters.id);
    } else if (filters.ids && Array.isArray(filters.ids) && filters.ids.length > 0) {
      query = query.in('id', filters.ids);
    } else {
      query = query.eq('status', 'active');
    }

    if (filters.categories && filters.categories.length) {
      query = query.contains('category', filters.categories[0]);
    }

    if (filters.jobType) {
      query = query.eq('job_type', filters.jobType);
    }

    if (filters.experienceLevel) {
      query = query.eq('experience_level', filters.experienceLevel);
    }

    if (filters.remoteOnly) {
      query = query.eq('remote_allowed', true);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;

    const processedData = data.map(job => {
      let category = [];
      if (typeof job.category === 'string') {
        try {
          category = JSON.parse(job.category);
        } catch {
          category = [];
        }
      } else if (Array.isArray(job.category)) {
        category = job.category;
      }
      
      let requirements = [];
      if (job.requirements) {
        if (typeof job.requirements === 'string') {
          try {
            requirements = JSON.parse(job.requirements);
          } catch {
            requirements = [];
          }
        } else if (Array.isArray(job.requirements)) {
          requirements = job.requirements;
        }
      }
      
      return {
        ...job,
        category,
        requirements,
        is_active: job.status === 'active'
      };
    });

    logger.success(`Recuperadas ${processedData.length} ofertas`);
    return processedData;
  } catch (error) {
    logger.error('Error obteniendo ofertas', error);
    throw error;
  }
};

const applyToJob = async (jobId, applicationData = {}) => {
  try {
    logger.info(`Aplicando a trabajo ${jobId}`);
    
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('Debes iniciar sesión para postularte');
    }

    // Verificar si ya se postuló
    const { data: existingApplication, error: checkError } = await supabase
      .from('job_applications')
      .select('*')
      .eq('job_id', jobId)
      .eq('applicant_id', user.id)
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      throw checkError;
    }
    
    if (existingApplication) {
      throw new Error('Ya te has postulado a esta oferta');
    }

    // Crear postulación
    const { data, error } = await supabase
      .from('job_applications')
      .insert({
        job_id: jobId,
        applicant_id: user.id,
        ...applicationData,
        status: 'pending',
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;

    logger.success('Postulación creada exitosamente');
    return data;
  } catch (error) {
    logger.error('Error en aplicación', error);
    throw error;
  }
};

const checkJobApplication = async (jobId) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) return false;

    const { data, error } = await supabase
      .from('job_applications')
      .select('*')
      .eq('job_id', jobId)
      .eq('applicant_id', user.id)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return !!data;
  } catch (error) {
    logger.error('Error verificando postulación', error);
    return false;
  }
};

const getUserJobApplications = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) return [];

    const { data, error } = await supabase
      .from('job_applications')
      .select(`
        *,
        job_offers (
          *,
          creator:company_profiles(company_name, logo)
        )
      `)
      .eq('applicant_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;

    const processedData = data.filter(app => app.job_offers).map(application => {
      let category = [];
      if (application.job_offers.category) {
        if (typeof application.job_offers.category === 'string') {
          try {
            category = JSON.parse(application.job_offers.category);
          } catch {
            category = [];
          }
        } else if (Array.isArray(application.job_offers.category)) {
          category = application.job_offers.category;
        }
      }
      
      return {
        ...application.job_offers,
        category,
        applicationStatus: application.status,
        applicationId: application.id,
        applicationDate: application.created_at,
        is_active: application.job_offers.status === 'active'
      };
    });

    return processedData;
  } catch (error) {
    logger.error('Error obteniendo postulaciones', error);
    throw error;
  }
};

const getJobById = async (jobId) => {
  try {
    const { data, error } = await supabase
      .from('job_offers')
      .select(`
        *,
        creator:company_profiles(company_name, logo)
      `)
      .eq('id', jobId)
      .single();
    
    if (error) throw error;
    if (!data) return null;
    
    let category = [];
    if (typeof data.category === 'string') {
      try {
        category = JSON.parse(data.category);
      } catch {
        category = [];
      }
    } else if (Array.isArray(data.category)) {
      category = data.category;
    }
    
    let requirements = [];
    if (data.requirements) {
      if (typeof data.requirements === 'string') {
        try {
          requirements = JSON.parse(data.requirements);
        } catch {
          requirements = [];
        }
      } else if (Array.isArray(data.requirements)) {
        requirements = data.requirements;
      }
    }
    
    return {
      ...data,
      category,
      requirements,
      is_active: data.status === 'active'
    };
  } catch (error) {
    logger.error('Error obteniendo trabajo por ID', error);
    throw error;
  }
};

export { 
  getJobListings, 
  applyToJob, 
  checkJobApplication, 
  getUserJobApplications,
  getJobById
};

export default {
  getJobListings,
  applyToJob,
  checkJobApplication,
  getUserJobApplications,
  getJobById
};